import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUp, ArrowDown, Trophy } from "lucide-react";

const short = (n) => (n || "").split(" ").slice(0, 2).join(" ");
const ROUND_LABEL = { T32: "Tabellone 32", T16: "Tabellone 16", T8: "Quarti", T4: "Semifinali", F: "Finale" };

function Slot({ k, nameOf, score, winner, seed, testid }) {
  const empty = !k;
  return (
    <div data-testid={testid} className={`flex items-center gap-2 px-2 h-9 text-sm ${winner ? "font-semibold bg-emerald-50" : ""} ${empty ? "text-zinc-300" : ""}`}>
      {seed && <span className="font-mono text-[10px] text-zinc-400 w-4">{seed}</span>}
      <span className="flex-1 truncate">{empty ? "—" : short(nameOf(k))}</span>
      {score != null && <span className="font-mono text-sm">{score}</span>}
    </div>
  );
}

export function BracketTab({ detail, nameOf, onBuild, onReset, onOpenBout }) {
  const { session, overall_ranking: overall, de } = detail;
  const n = overall.length || session.participants.length;
  const defaultSize = [2, 4, 8, 16, 32].find((s) => s >= n) || 32;
  const [size, setSize] = useState(defaultSize);
  const [hits, setHits] = useState(session.de_hits || 15);
  const [seeding, setSeeding] = useState(() => (overall.length ? overall.map((r) => r.key) : session.participants.map((p) => p.key)));
  const seedIndex = useMemo(() => Object.fromEntries(seeding.map((k, i) => [k, i + 1])), [seeding]);

  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= seeding.length) return;
    const s = [...seeding]; [s[i], s[j]] = [s[j], s[i]]; setSeeding(s);
  };

  if (!de.length) {
    return (
      <div className="grid lg:grid-cols-2 gap-6" data-testid="bracket-setup">
        <div className="bg-white border border-zinc-200 rounded-md p-4 space-y-3">
          <div className="font-heading font-semibold text-sm">Tabellone di eliminazione diretta</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Dimensione</Label>
              <Select value={String(size)} onValueChange={(v) => setSize(parseInt(v))}>
                <SelectTrigger data-testid="de-size"><SelectValue /></SelectTrigger>
                <SelectContent>{[2, 4, 8, 16, 32].filter((s) => s >= Math.min(seeding.length, 32) || s === defaultSize).map((s) => <SelectItem key={s} value={String(s)}>Tabellone {s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Stoccate</Label>
              <Select value={String(hits)} onValueChange={(v) => setHits(parseInt(v))}>
                <SelectTrigger data-testid="de-hits"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="10">10</SelectItem><SelectItem value="15">15</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-zinc-500">{seeding.length} qualificati su {size} posti: {Math.max(0, size - seeding.length)} bye alle prime teste di serie.</p>
          <Button data-testid="de-build" disabled={seeding.length < 2 || seeding.length > size} onClick={() => onBuild({ size, hits, seeding })} className="bg-black hover:bg-zinc-800">Genera tabellone</Button>
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-4">
          <div className="font-heading font-semibold text-sm mb-1">Seeding <span className="text-xs text-zinc-500 font-normal">· dalla classifica gironi, modificabile</span></div>
          <div className="divide-y divide-zinc-100" data-testid="de-seeding">
            {seeding.map((k, i) => (
              <div key={k} className="flex items-center gap-2 py-1.5 text-sm" data-testid={`seed-${k}`}>
                <span className="font-mono text-xs text-zinc-400 w-6 text-right">{i + 1}</span>
                <span className="flex-1 truncate">{nameOf(k)}</span>
                <button data-testid={`seed-up-${k}`} onClick={() => move(i, -1)} className="p-1 text-zinc-400 hover:text-black disabled:opacity-30" disabled={i === 0}><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`seed-down-${k}`} onClick={() => move(i, 1)} className="p-1 text-zinc-400 hover:text-black disabled:opacity-30" disabled={i === seeding.length - 1}><ArrowDown className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const rounds = [...new Set(de.map((m) => m.round_index))].sort((a, b) => a - b);
  const final = de.find((m) => m.round === "F");
  return (
    <div className="space-y-4" data-testid="bracket">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-zinc-600">Tabellone {de.filter((m) => m.round_index === 0).length * 2} · {de[0]?.hits || session.de_hits} stoccate · clicca un incontro per il risultato</div>
        <div className="flex items-center gap-3">
          {final?.winner_key && <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-700" data-testid="de-champion"><Trophy className="w-4 h-4" />{nameOf(final.winner_key)}</div>}
          <Button variant="outline" size="sm" data-testid="de-reset" onClick={() => window.confirm("Azzerare il tabellone e i suoi assalti?") && onReset()}>Rifai tabellone</Button>
        </div>
      </div>
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-6 min-w-max items-stretch">
          {rounds.map((ri) => {
            const ms = de.filter((m) => m.round_index === ri).sort((a, b) => a.position - b.position);
            return (
              <div key={ri} className="flex flex-col justify-around w-56" data-testid={`de-round-${ms[0].round}`}>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 text-center">{ROUND_LABEL[ms[0].round] || ms[0].round}</div>
                <div className="flex-1 flex flex-col justify-around gap-3">
                  {ms.map((m) => {
                    const b = m.bout; const done = b?.status === "completed";
                    const sA = done ? (b.participant_a === m.a_key ? b.score_for : b.score_against) : null;
                    const sB = done ? (b.participant_a === m.b_key ? b.score_for : b.score_against) : null;
                    const clickable = !!b;
                    return (
                      <button key={m.id} data-testid={`de-match-${m.round}-${m.position}`} disabled={!clickable} onClick={() => clickable && onOpenBout(b, m)} className={`w-full text-left border rounded-md overflow-hidden divide-y divide-zinc-100 bg-white transition ${clickable ? "border-zinc-300 hover:border-black cursor-pointer" : "border-zinc-200 opacity-80"}`}>
                        <Slot k={m.a_key} nameOf={nameOf} score={sA} winner={m.winner_key && m.winner_key === m.a_key} seed={seedIndex[m.a_key]} testid={`de-slot-${m.round}-${m.position}-a`} />
                        <Slot k={m.b_key} nameOf={nameOf} score={sB} winner={m.winner_key && m.winner_key === m.b_key} seed={seedIndex[m.b_key]} testid={`de-slot-${m.round}-${m.position}-b`} />
                        {!m.a_key !== !m.b_key && !b && <div className="px-2 py-0.5 text-[10px] uppercase text-zinc-400 bg-zinc-50">bye</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
